/* What every control is handed, and the label/error frame around it. */
import type { ReactNode } from 'react'
import type { DocField, Doc } from '../types'

export type ControlProps = {
  field: DocField
  value: unknown
  doc: Doc
  /** The whole document, for a child row: `parent.company` appears in
   *  conditions on child tables. */
  parent?: Doc
  readOnly: boolean
  required: boolean
  error?: string
  onChange: (value: unknown) => void
  /** Currency and Float read their precision and currency from a sibling
   *  field, which only the form knows how to resolve. */
  currency?: string
  autoFocus?: boolean
  /** Inside a grid the control is bare: no label, no description. */
  bare?: boolean
}

export type Control = (props: ControlProps) => ReactNode

export function Field({
  field, required, error, children, bare,
}: { field: DocField; required: boolean; error?: string; children: ReactNode; bare?: boolean }) {
  if (bare) return <>{children}</>
  const id = `f_${field.fieldname}`
  return (
    <div className="field">
      <label htmlFor={id}>
        {field.label || field.fieldname}
        {required ? <span className="req" aria-label="required">*</span> : null}
      </label>
      {children}
      {error ? (
        <div className="err" role="alert">
          <Warn /> {error}
        </div>
      ) : field.description ? (
        <div className="hint">{field.description}</div>
      ) : null}
    </div>
  )
}

const Warn = () => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
    <path d="M8 2 15 14H1L8 2Z" strokeLinejoin="round" /><path d="M8 6.5v3.5M8 12h.01" strokeLinecap="round" />
  </svg>
)

/** Frappe stores a Check as 0/1, not a boolean, and the wire format matters. */
export const asCheck = (v: unknown) => (v === 1 || v === true || v === '1' ? 1 : 0)
export const asText = (v: unknown) => (v === null || v === undefined ? '' : String(v))
export const asNumber = (v: unknown) => {
  if (v === '' || v === null || v === undefined) return ''
  const n = Number(v)
  return Number.isNaN(n) ? '' : n
}
